/**
 * <fiee-dev-reload> — development-only live-reload island.
 *
 * The server renders this element into the layout only when APP_ENV=dev (see
 * handler.LiveReload). It holds an SSE connection to `/_dev/reload`; when the
 * server restarts (e.g. after `make live` rebuilds) the stream drops, and the
 * component polls the current URL until the fresh server answers, then reloads.
 *
 * Polling on disconnect — rather than waiting for EventSource's slow built-in
 * reconnect — is what makes the reload feel instant.
 */
export class DevReload extends HTMLElement {
  static readonly #endpoint = "/_dev/reload";
  static readonly #pollMs = 100;

  #sse: EventSource | null = null;
  #pollTimer: number | undefined;
  #connected = false;

  connectedCallback(): void {
    this.hidden = true;
    this.#connect();
  }

  disconnectedCallback(): void {
    this.#cleanup();
  }

  #connect(): void {
    this.#sse = new EventSource(DevReload.#endpoint);

    this.#sse.onopen = (): void => {
      this.#connected = true;
      console.info("[dev-reload] connected");
    };

    this.#sse.onerror = (): void => {
      this.#sse?.close();
      this.#sse = null;

      // A drop before we ever connected means the endpoint isn't there; don't
      // spin. Once we've been connected, a drop means the server is rebuilding.
      if (!this.#connected) {
        console.error("[dev-reload] could not reach dev server");
        return;
      }
      console.info("[dev-reload] server gone, waiting for restart…");
      this.#startPolling();
    };
  }

  #startPolling(): void {
    if (this.#pollTimer !== undefined) return;

    this.#pollTimer = window.setInterval(() => {
      fetch(window.location.href, { method: "HEAD" })
        .then((res: Response) => {
          if (res.ok) {
            this.#cleanup();
            window.location.reload();
          }
        })
        .catch(() => {});
    }, DevReload.#pollMs);
  }

  #cleanup(): void {
    this.#sse?.close();
    this.#sse = null;
    if (this.#pollTimer !== undefined) {
      window.clearInterval(this.#pollTimer);
      this.#pollTimer = undefined;
    }
  }
}
